import React, { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { usePermissions } from '../../hooks';
import { useNavigate } from 'react-router';
import { useNotification } from '@refinedev/core';
import { useTranslate } from '@refinedev/core';
import ServerPagination from '../../components/pagination/ServerPagination';
import { apiVersion, apiJson } from '../../utils/api';
import {
    DeletePlaceDialog,
    EditNotesDialog,
    PlaceCard,
    PlacesFilterBar
} from './components';
// Map snippet removed

// Mock removed; using server endpoint

export const PlacesList: React.FC = () => {
    const { hasFullAccess } = usePermissions();
    const { open: notify } = useNotification();
    const t = useTranslate();
    const [searchText, setSearchText] = useState('');
    const [filterType, setFilterType] = useState('all');
    const [photoIndexes, setPhotoIndexes] = useState<{ [key: string]: number }>({});
    const [expandedDescriptions, setExpandedDescriptions] = useState<Set<string>>(new Set());
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [editingPlace, setEditingPlace] = useState<any>(null);
    const [editingDescription, setEditingDescription] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();

    // Server-side filters via endpoint; keep local state only

    const handleCreatePlaces = () => {
        navigate('/places/new');
    };

    const handlePhotoNavigation = (placeId: string, direction: 'prev' | 'next', totalPhotos: number) => {
        const currentIndex = photoIndexes[placeId] || 0;
        if (!totalPhotos || totalPhotos <= 0) return;
        let newIndex;

        if (direction === 'prev') {
            newIndex = currentIndex === 0 ? totalPhotos - 1 : currentIndex - 1;
        } else {
            newIndex = currentIndex === totalPhotos - 1 ? 0 : currentIndex + 1;
        }

        setPhotoIndexes(prev => ({ ...prev, [placeId]: newIndex }));
    };

    const toggleDescriptionExpanded = (placeId: string) => {
        setExpandedDescriptions(prev => {
            const newSet = new Set(prev);
            if (newSet.has(placeId)) {
                newSet.delete(placeId);
            } else {
                newSet.add(placeId);
            }
            return newSet;
        });
    };

    const handleEditDescription = (place: any, refetch?: () => void) => {
        setEditingPlace({ ...place, refetch });
        setEditingDescription(place.notes || '');
        setEditDialogOpen(true);
    };

    const handleDeletePlace = (place: any, refetch?: () => void) => {
        setEditingPlace({ ...place, refetch });
        setDeleteDialogOpen(true);
    };

    const handleSaveDescription = async () => {
        if (!editingPlace) return;

        setIsLoading(true);
        try {
            await apiJson(`admin/${apiVersion}/places/${editingPlace.id}/notes`, {
                method: 'PUT',
                body: JSON.stringify({
                    notes: editingDescription.trim()
                })
            });

            notify?.({
                type: 'success',
                message: t('places.notifications.updateSuccess')
            });

            setEditDialogOpen(false);
            setEditingPlace(null);
            setEditingDescription('');

            // Refresh the data
            if (editingPlace.refetch) {
                await editingPlace.refetch();
            }
        } catch (error) {
            notify?.({
                type: 'error',
                message: t('places.notifications.updateError')
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleConfirmDelete = async () => {
        if (!editingPlace) return;

        setIsLoading(true);
        try {
            await apiJson(`admin/${apiVersion}/places/${editingPlace.id}`, {
                method: 'DELETE'
            });

            notify?.({
                type: 'success',
                message: t('places.notifications.deleteSuccess')
            });

            setDeleteDialogOpen(false);
            setEditingPlace(null);

            // Refresh the data
            if (editingPlace.refetch) {
                await editingPlace.refetch();
            }
        } catch (error) {
            notify?.({
                type: 'error',
                message: t('places.notifications.deleteError')
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                padding: 2
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5">{t('places.titles.list')}</Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleCreatePlaces}
                    sx={{ borderRadius: 2 }}
                >
                    {t('places.actions.createPlace')}
                </Button>
            </Box>

            <PlacesFilterBar
                searchText={searchText}
                filterType={filterType}
                onSearchChange={setSearchText}
                onFilterChange={value => setFilterType(value)}
                t={t}
            />

            <ServerPagination<any>
                endpoint={`admin/${apiVersion}/places`}
                search={searchText.length >= 3 ? searchText : ''}
                pageSizeOptions={[5, 10, 15, 20]}
                extraParams={{ type: filterType === 'all' ? undefined : filterType }}
            >
                {(items, helpers) => (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {items.map(place => {
                            const pid = (place as any).id;
                            const loc = (place as any).location ?? {};
                            const photos: any[] = Array.isArray(loc.photos) ? loc.photos : [];
                            const totalPhotos = photos.length || 0;

                            return (
                                <PlaceCard
                                    key={pid}
                                    place={place}
                                    hasFullAccess={hasFullAccess}
                                    photoIndex={photoIndexes[pid] || 0}
                                    onNavigatePhoto={(direction, total) =>
                                        handlePhotoNavigation(pid, direction, total || totalPhotos)
                                    }
                                    isDescriptionExpanded={expandedDescriptions.has(pid)}
                                    onToggleDescription={() => toggleDescriptionExpanded(pid)}
                                    onEdit={() => handleEditDescription(place, helpers?.refetch)}
                                    onDelete={() => handleDeletePlace(place, helpers?.refetch)}
                                    t={t}
                                />
                            );
                        })}
                    </Box>
                )}
            </ServerPagination>

            <EditNotesDialog
                open={editDialogOpen}
                value={editingDescription}
                onChange={setEditingDescription}
                onClose={() => setEditDialogOpen(false)}
                onSave={handleSaveDescription}
                isLoading={isLoading}
                t={t}
            />

            <DeletePlaceDialog
                open={deleteDialogOpen}
                placeName={editingPlace?.location?.name}
                onClose={() => setDeleteDialogOpen(false)}
                onConfirm={handleConfirmDelete}
                isLoading={isLoading}
                t={t}
            />
        </Box>
    );
};
